import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { AddCircleOutline, CloseRounded } from '@mui/icons-material'
import { useState } from 'react'
import { useGetOrderRequests, TitleOrderRequest } from '../../logic/useApiQuery'
import { useCancelOrderRequest } from '../../logic/useApiMutation'
import { useGetUserConfig } from '../../logic/useUserConfig'
import { OrderRequestSendToOption } from '../../types/portalTypes/PortalDomainOrg'
import { OrderRequestForm } from './OrderRequestForm'

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  COMPLETE: 'success',
  ERROR: 'error',
  CANCELLED: 'default',
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {children}
      </Box>
      <Divider sx={{ mt: 2 }} />
    </Box>
  )
}

function DetailFieldFull({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <Box sx={{ mb: 1, gridColumn: '1 / -1' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  )
}

function RequestDetailDialog({ req, onClose, onCancel, sendToOptions }: { req: TitleOrderRequest; onClose: () => void; onCancel: () => void; sendToOptions: OrderRequestSendToOption[] }) {
  const cancelMutation = useCancelOrderRequest(req.RequestId)

  async function handleCancel() {
    await cancelMutation.mutateAsync(undefined)
    onCancel()
  }
  const rawLoanAmount = req.LenderInfo?.loanAmount
  const loanAmountNum = rawLoanAmount ? Number(String(rawLoanAmount).replace(/[^\d.]/g, '')) : NaN
  const loanAmount = rawLoanAmount
    ? Number.isFinite(loanAmountNum) ? `$${loanAmountNum.toLocaleString()}` : String(rawLoanAmount)
    : undefined

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6">Order Request Details</Typography>
          <Typography variant="caption" color="text.secondary">ID: {req.RequestId}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={req.Status} color={STATUS_COLORS[req.Status] ?? 'default'} size="small" />
          <IconButton onClick={onClose} size="small"><CloseRounded /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>

        <DetailSection title="Transaction Information">
          <DetailField label="Order Type" value={req.OrderType} />
          <DetailField label="Closing Date" value={req.ClosingDate} />
          <DetailField label="Submitted" value={new Date(req.CreatedAt).toLocaleString()} />
          <DetailField label="Route To" value={sendToOptions.find((o) => o.id === req.RouteToId)?.name ?? req.RouteToId} />
        </DetailSection>

        {req.RequestedBy && (
          <DetailSection title="Requested By">
            <DetailField label="Role" value={req.RequestedBy.role} />
            <DetailField label="Name" value={`${req.RequestedBy.firstName} ${req.RequestedBy.lastName}`} />
            <DetailField label="Email" value={req.RequestedBy.email} />
            <DetailField label="Phone" value={req.RequestedBy.phone} />
            <DetailField label="Fax" value={req.RequestedBy.fax} />
            <DetailField label="Company" value={req.RequestedBy.companyName} />
            <DetailFieldFull
              label="Company Address"
              value={[req.RequestedBy.companyAddress1, req.RequestedBy.companyAddress2, req.RequestedBy.companyCity, req.RequestedBy.companyState, req.RequestedBy.companyZip].filter(Boolean).join(', ') || null}
            />
          </DetailSection>
        )}

        {req.PropertyInfo && (
          <DetailSection title="Property Information">
            <DetailFieldFull label="Address" value={[req.PropertyInfo.address1, req.PropertyInfo.address2].filter(Boolean).join(' ')} />
            <DetailField label="City" value={req.PropertyInfo.city} />
            <DetailField label="State / Zip" value={`${req.PropertyInfo.state} ${req.PropertyInfo.zip}`} />
            <DetailField label="Tax ID" value={req.PropertyInfo.taxId} />
            <DetailField label="Vacant Land" value={req.PropertyInfo.vacantLand !== undefined ? (req.PropertyInfo.vacantLand ? 'Yes' : 'No') : null} />
            <DetailFieldFull label="Legal Description" value={req.PropertyInfo.legalDescription} />
          </DetailSection>
        )}

        {req.Borrowers?.length ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>
              {`Borrower${req.Borrowers.length > 1 ? 's' : ''}`}
            </Typography>
            {req.Borrowers.map((b, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                {req.Borrowers!.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Borrower {i + 1}</Typography>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                  <DetailField label="Name" value={[b.firstName, b.middleName, b.lastName].filter(Boolean).join(' ')} />
                  <DetailField label="Type" value={b.type.charAt(0).toUpperCase() + b.type.slice(1)} />
                  <DetailField label="Email" value={b.email} />
                  <DetailField label="Phone" value={b.phone} />
                  {b.contactAddress && (
                    <DetailFieldFull label="Contact Address" value={[b.contactAddress.address1, b.contactAddress.address2, b.contactAddress.city, b.contactAddress.state, b.contactAddress.zip].filter(Boolean).join(', ')} />
                  )}
                </Box>
                {i < req.Borrowers!.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
            <Divider />
          </Box>
        ) : null}

        {req.LenderInfo && Object.values(req.LenderInfo).some(Boolean) && (
          <DetailSection title="Lender Information">
            <DetailField label="Lender Name" value={req.LenderInfo.lenderName} />
            <DetailField label="Financing Type" value={req.LenderInfo.financingType} />
            <DetailField label="Loan Amount" value={loanAmount} />
            <DetailField label="Loan Number" value={req.LenderInfo.loanNumber} />
            <DetailField label="Lender Contact" value={[req.LenderInfo.lenderFirstName, req.LenderInfo.lenderLastName].filter(Boolean).join(' ') || null} />
            <DetailField label="Lender Email" value={req.LenderInfo.lenderEmail} />
            <DetailField label="Lender Phone" value={req.LenderInfo.lenderPhone} />
          </DetailSection>
        )}

        {req.Comments && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Comments</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{req.Comments}</Typography>
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {req.Documents.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Documents ({req.Documents.length})</Typography>
            {req.Documents.map((doc, i) => (
              <Typography key={i} variant="body2" sx={{ py: 0.5, borderBottom: i < req.Documents.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                {doc.OriginalFileName}
              </Typography>
            ))}
          </Box>
        )}

      </DialogContent>
      <DialogActions>
        {req.Status === 'PENDING' && (
          <Button color="error" onClick={handleCancel} disabled={cancelMutation.isLoading}>
            {cancelMutation.isLoading ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export function OrderRequestPage() {
  const [showForm, setShowForm] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [lastSubmittedId, setLastSubmittedId] = useState<string | null>(null)
  const [selectedRequest, setSelectedRequest] = useState<TitleOrderRequest | null>(null)

  const { data, isLoading, refetch } = useGetOrderRequests()
  const { data: userConfig } = useGetUserConfig()
  const sendToOptions = userConfig?.uiConfig?.OrderRequestSendToOptions ?? []
  const orderRequests = data?.orderRequests ?? []

  function handleSuccess(requestId: string) {
    setLastSubmittedId(requestId)
    setShowForm(false)
    refetch()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto', mt: '70px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Title Order Requests
        </Typography>
        {!showForm && (
          <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => { setShowForm(true); setFormKey((k) => k + 1); setLastSubmittedId(null) }}>
            New Request
          </Button>
        )}
      </Box>

      {lastSubmittedId && !showForm && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your title order request has been submitted successfully. A team member will follow up with you shortly.
        </Alert>
      )}

      {showForm ? (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>New Title Order Request</Typography>
          <Divider sx={{ mb: 2 }} />
          <OrderRequestForm key={formKey} onSuccess={handleSuccess} sendToOptions={sendToOptions} />
          <Button sx={{ mt: 1 }} onClick={() => setShowForm(false)}>Cancel</Button>
        </>
      ) : (
        <>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : orderRequests.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                No order requests yet.
              </Typography>
              <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => { setShowForm(true); setFormKey((k) => k + 1) }}>
                Submit Your First Request
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper} elevation={8} sx={{ borderRadius: '20px', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Order Type</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Borrower(s)</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderRequests.map((req) => (
                    <TableRow
                      key={req.RequestId}
                      hover
                      onClick={() => setSelectedRequest(req)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{new Date(req.CreatedAt).toLocaleDateString()}</TableCell>
                      <TableCell>{req.OrderType ?? '—'}</TableCell>
                      <TableCell>
                        {req.PropertyInfo
                          ? <><Typography variant="body2">{req.PropertyInfo.address1}</Typography><Typography variant="caption" color="text.secondary">{req.PropertyInfo.city}, {req.PropertyInfo.state} {req.PropertyInfo.zip}</Typography></>
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {req.Borrowers?.length
                          ? req.Borrowers.map((b, i) => <Typography key={i} variant="body2">{b.firstName} {b.lastName}</Typography>)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.Status}
                          color={STATUS_COLORS[req.Status] ?? 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {selectedRequest && (
        <RequestDetailDialog
          req={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onCancel={() => { setSelectedRequest(null); refetch() }}
          sendToOptions={sendToOptions}
        />
      )}
    </Box>
  )
}
